import { useEffect, useRef, useState } from 'react'
import type { DrawerTarget } from '../ObjectDrawer'
import { documentReaderHref, type DocumentRef } from '../../lib/documentReader'
import {
  formatOfficerDate,
  formatOfficerDateTime,
  formatNumberString,
  humanizeToken,
} from '../../lib/officerFormat'
import { ErrorState, Skeleton } from '../state'
import { sourceDossierUrl, type AdditionalObjectAccess, type ViewScope } from './context'
import { useRead } from './useRead'
import { useAuthSession } from '../../lib/authSession'
import ObjectRecordEditor from './ObjectRecordEditor'

interface Dossier {
  target_type: string
  target_id: string
  title: string
  subtitle: string | null
  origin: string
  facts: Record<string, string | number | boolean | null>
  source_documents: DocumentRef[]
  source_clients?: { id: string; display_name: string | null; folder_reference: string }[]
  history: {
    id: string
    event_type: string
    user_name: string | null
    recorded_at: string
    effective_date: string | null
  }[]
  read_coverage: 'owned_sources_and_retained_history'
}

// These keys come from the entity/instrument object-drawer facts contract.
// Unknown facts remain literal: a registry ID or source text is not an amount or enum.
const DATE_FACTS = new Set(['incorporated', 'balance as of', 'issued', 'matures'])
const NUMBER_FACTS = new Set(['outstanding units'])
const ENUM_FACTS = new Set(['nature', 'category'])

function fact(key: string, value: string | number | boolean | null) {
  if (value === null || value === '') return 'Not recorded'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  const text = String(value)
  if (DATE_FACTS.has(key) && /^\d{4}-\d{2}-\d{2}$/.test(text)) return formatOfficerDate(text)
  if (NUMBER_FACTS.has(key)) return formatNumberString(text)
  if (ENUM_FACTS.has(key)) return humanizeToken(text)
  return text
}
function historyLabel(type: string) {
  const parts = type.split('.')
  const action = parts[parts.length - 1]
  return (
    (
      {
        created: 'Recorded',
        updated: 'Updated',
        corroborated: 'Corroborated',
        historical_reference: 'Reference retained',
      } as Record<string, string>
    )[action ?? ''] ?? 'Recorded activity'
  )
}

/** One already-scoped response. No evidence-status, title, task or fund fallback requests. */
export default function SourceObjectDossier({
  access,
  target,
  scope,
  returnTo,
  allowEdits = false,
  onRecordChanged,
  onClose,
}: {
  access: AdditionalObjectAccess
  target: DrawerTarget
  scope: ViewScope
  returnTo: string
  allowEdits?: boolean
  onRecordChanged?: () => void
  onClose: () => void
}) {
  const [retry, setRetry] = useState(0)
  const panel = useRef<HTMLElement | null>(null)
  const close = useRef(onClose)
  close.current = onClose
  const url = sourceDossierUrl(access, target, scope)
  const read = useRead<Dossier>(url, retry)
  const session = useAuthSession()
  const viewKey = JSON.stringify([url, session])
  const [retained, setRetained] = useState<{ key: string; value: Dossier } | null>(null)
  const [chosenClient, setChosenClient] = useState<string | null>(null)
  const [editorBusy, setEditorBusy] = useState(false)
  useEffect(() => {
    if (read.error) setRetained(null)
    else if (read.value) setRetained({ key: viewKey, value: read.value })
  }, [read.error, read.value, viewKey])
  const value =
    read.value ?? (read.loading && retained?.key === viewKey ? retained.value : undefined)
  const data =
    value?.target_type === target.type &&
    value.target_id === target.id &&
    value.read_coverage === 'owned_sources_and_retained_history'
      ? value
      : null
  const clients = (data?.source_clients ?? []).filter(
    (client) =>
      access.client_ids.includes(client.id) &&
      (scope.kind !== 'client' || scope.client_id === client.id)
  )
  const editClient =
    clients.find((client) => client.id === chosenClient) ??
    (clients.length === 1 ? clients[0] : null)
  useEffect(() => {
    const opener = document.activeElement
    const element = panel.current
    element?.focus()
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return
      event.preventDefault()
      close.current()
    }
    window.addEventListener('keydown', escape)
    return () => {
      window.removeEventListener('keydown', escape)
      if (
        opener instanceof HTMLElement &&
        opener.isConnected &&
        (document.activeElement === document.body || element?.contains(document.activeElement))
      )
        opener.focus()
    }
  }, [])
  return (
    <>
      <div className="fa-scrim" aria-hidden="true" />
      <aside
        ref={panel}
        role="dialog"
        aria-label={data?.title ?? 'Recorded object'}
        tabIndex={-1}
        className="fa-drawer"
        data-testid="source-object-dossier"
      >
        <div className="drawer-body">
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span className="tag">{target.type}</span>
            <button
              type="button"
              className="vbtn"
              onClick={onClose}
              style={{ marginLeft: 'auto' }}
              aria-label="Close recorded object"
            >
              ✕
            </button>
          </div>
          {!url || (read.value && !data) ? (
            <p role="status">This object's details are unavailable in this view.</p>
          ) : null}
          {read.loading ? <Skeleton kind="lines" n={5} /> : null}
          {read.error ? (
            <>
              <p>This object's details are unavailable in this view.</p>
              <ErrorState
                what="recorded object"
                error={read.error}
                onRetry={() => setRetry((n) => n + 1)}
                compact
              />
            </>
          ) : null}
          {data ? (
            <>
              <h2 className="drawer-title">{data.title}</h2>
              {data.subtitle ? <p className="nav-muted">{data.subtitle}</p> : null}
              {allowEdits && clients.length > 1 ? (
                <div className="nav-context-links" aria-label="Choose client context for editing">
                  <label>
                    Client
                    <select
                      aria-label="Client for Record edit"
                      disabled={editorBusy}
                      value={editClient?.id ?? ''}
                      onChange={(event) => setChosenClient(event.target.value || null)}
                    >
                      <option value="">Choose client…</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.display_name ??
                            `Client name not recorded · ${client.folder_reference}`}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : null}
              {allowEdits && editClient ? (
                <ObjectRecordEditor
                  target={{ kind: target.type, id: target.id, label: data.title }}
                  scope={{ client_id: editClient.id }}
                  documents={data.source_documents}
                  onBusyChange={setEditorBusy}
                  onRecordChanged={() => {
                    setRetry((n) => n + 1)
                    onRecordChanged?.()
                  }}
                />
              ) : null}
              <div className="drawer-sec first">Current record</div>
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr)',
                  gap: '8px 14px',
                  margin: 0,
                }}
              >
                {Object.entries(data.facts).map(([key, value]) => (
                  <div key={key} style={{ display: 'contents' }}>
                    <dt className="nav-muted">{humanizeToken(key)}</dt>
                    <dd style={{ margin: 0, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
                      {fact(key, value)}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="drawer-sec">Source documents</div>
              {data.source_documents?.length ? (
                data.source_documents.map((document) => (
                  <div className="drawer-ln" key={document.document_id}>
                    <a
                      className="drawer-evlink"
                      href={documentReaderHref(document.document_id, {
                        returnTo,
                        page: document.page,
                      })}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="source-dossier-document"
                    >
                      {document.display_title ?? 'Source document'} ›
                    </a>
                  </div>
                ))
              ) : (
                <p className="nav-muted">No current source documents are available.</p>
              )}
              <details>
                <summary className="drawer-sec">Retained history</summary>
                {data.history.map((entry) => (
                  <div className="drawer-ln" key={entry.id}>
                    <strong>{historyLabel(entry.event_type)}</strong>
                    <div>
                      {formatOfficerDateTime(entry.recorded_at)}
                      {entry.user_name ? ` · ${entry.user_name}` : ''}
                    </div>
                    {entry.effective_date ? (
                      <div>Effective {formatOfficerDate(entry.effective_date)}</div>
                    ) : null}
                  </div>
                ))}
                {!data.history.length ? (
                  <p className="nav-muted">No retained events are available.</p>
                ) : null}
              </details>
              <p className="nav-muted">
                Only retained history is shown. Earlier activity may be unavailable.
              </p>
            </>
          ) : null}
        </div>
      </aside>
    </>
  )
}
